import { useState, type FormEvent, type KeyboardEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router-dom'
import { Alert, Box, Button, Chip, IconButton, Menu, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material'
import Add from '@mui/icons-material/Add'
import ArrowBack from '@mui/icons-material/ArrowBack'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import DragIndicator from '@mui/icons-material/DragIndicator'
import EditOutlined from '@mui/icons-material/EditOutlined'
import {
  BANDS_QUERY_KEY,
  catalogError,
  createBand,
  createFunction,
  deleteBand,
  deleteFunction,
  fetchBands,
  maiuscula,
  updateBand,
  updateFunction,
  type JobBand,
  type JobFunction,
} from '../api/bands'
import { ORG_CHART_QUERY_KEY } from '../api/orgChart'
import { fetchMe, ME_QUERY_KEY } from '../api/session'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { brand } from '../theme'
import { ConfirmDialog, type Confirmacao } from '../components/ConfirmDialog'
import { t } from '../i18n'

/**
 * s62 / C3b-2 -- tela "Faixas e funcoes" (ADR-0032; Marcelo 20/10/2026: F0 faixas por empresa, F0a bloqueia, F0b 1-12,
 * F1 funcoes por empresa, F4 ARRASTAR). Cada faixa e uma LINHA horizontal (como no organograma); as funcoes sao cartoes
 * dentro da linha. Arrastar um cartao para outra linha MUDA a faixa da funcao (e de todos os cargos com ela); arrastar
 * a alca da linha reordena as faixas. Tudo tem tambem um caminho SEM arrastar (setas, menu "Mover para") -- teclado e
 * celular. So quem altera cargos (job_architecture.job.write) ve os controles; quem so le ve as linhas.
 */

const PERM_WRITE = 'job_architecture.job.write'
const BAND_MAX = 12

type Arraste = { tipo: 'funcao'; id: string; de: string } | { tipo: 'faixa'; id: string } | null

function mensagem(e: unknown): string {
  const { code, count } = catalogError(e)
  const m = t.bands.errors
  if (code === 'band_has_functions') return m.bandHasFunctions(count ?? 0)
  if (code === 'job_function_in_use') return m.functionInUse(count ?? 0)
  if (code === 'catalog_name_already_exists') return m.nameExists
  if (code === 'invalid_catalog_name') return m.invalidName
  if (code === 'band_limit_reached') return m.bandLimit
  if (code === 'last_band_cannot_be_removed') return m.lastBand
  return m.generic
}

function NomeEditavel({ valor, rotulo, onSalvar }: { valor: string; rotulo: string; onSalvar: (v: string) => void }) {
  const [texto, setTexto] = useState(valor)
  const teclas = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onSalvar(texto)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onSalvar(valor)
    }
  }
  return (
    <TextField
      autoFocus
      size="small"
      value={texto}
      onChange={(e) => setTexto(e.target.value)}
      onKeyDown={teclas}
      onBlur={() => onSalvar(texto)}
      slotProps={{ htmlInput: { maxLength: 80, 'aria-label': rotulo } }}
      sx={{ minWidth: 200 }}
    />
  )
}

function CartaoFuncao({
  f,
  faixas,
  pode,
  onArrastar,
  onMover,
  onRenomear,
  onExcluir,
}: {
  f: JobFunction
  faixas: readonly JobBand[]
  pode: boolean
  onArrastar: () => void
  onMover: (bandId: string) => void
  onRenomear: (nome: string) => void
  onExcluir: () => void
}) {
  const [menu, setMenu] = useState<HTMLElement | null>(null)
  const [editando, setEditando] = useState(false)
  if (editando)
    return (
      <NomeEditavel
        valor={f.name}
        rotulo={t.bands.renameFunction(f.name)}
        onSalvar={(v) => {
          setEditando(false)
          if (v.trim() !== '' && v.trim() !== f.name) onRenomear(v)
        }}
      />
    )
  return (
    <>
      <Chip
        data-testid={`funcao-${f.id}`}
        draggable={pode}
        onDragStart={(e) => {
          e.dataTransfer?.setData('text/plain', f.id)
          onArrastar()
        }}
        label={
          <span>
            <strong>{maiuscula(f.name)}</strong>
            <Box component="span" sx={{ color: 'text.secondary', ml: 0.75 }}>
              {t.bands.jobsCount(f.jobsCount)}
            </Box>
          </span>
        }
        onClick={pode ? (e) => setMenu(e.currentTarget) : undefined}
        aria-label={pode ? t.bands.functionActions(f.name) : undefined}
        variant="outlined"
        sx={{ bgcolor: '#FFFFFF', borderColor: '#CBD5E1', cursor: pode ? 'grab' : 'default', maxWidth: '100%' }}
      />
      <Menu anchorEl={menu} open={menu !== null} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            setMenu(null)
            setEditando(true)
          }}
        >
          {t.bands.rename}
        </MenuItem>
        {faixas
          .filter((b) => b.id !== f.bandId)
          .map((b) => (
            <MenuItem
              key={b.id}
              onClick={() => {
                setMenu(null)
                onMover(b.id)
              }}
            >
              {t.bands.moveTo(b.name)}
            </MenuItem>
          ))}
        <MenuItem
          onClick={() => {
            setMenu(null)
            onExcluir()
          }}
          sx={{ color: 'error.main' }}
        >
          {t.bands.deleteFunction}
        </MenuItem>
      </Menu>
    </>
  )
}

function NovaFuncao({ onCriar }: { onCriar: (nome: string) => void }) {
  const [nome, setNome] = useState('')
  const enviar = (e: FormEvent) => {
    e.preventDefault()
    if (nome.trim() === '') return
    onCriar(nome)
    setNome('')
  }
  return (
    <Box component="form" onSubmit={enviar} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <TextField
        size="small"
        placeholder={t.bands.newFunction}
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        slotProps={{ htmlInput: { maxLength: 80, 'aria-label': t.bands.newFunction } }}
        sx={{ width: 170 }}
      />
      <IconButton type="submit" size="small" aria-label={t.bands.addFunction}>
        <Add fontSize="small" />
      </IconButton>
    </Box>
  )
}

export function BandsPage() {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe })
  const q = useQuery({ queryKey: BANDS_QUERY_KEY, queryFn: fetchBands, retry: false })
  const pode = (me.data?.permissions ?? []).includes(PERM_WRITE)
  const [erro, setErro] = useState<string | null>(null)
  const [arraste, setArraste] = useState<Arraste>(null)
  const [renomeando, setRenomeando] = useState<string | null>(null)
  const [novaFaixa, setNovaFaixa] = useState('')
  // s69 / 2s (Marcelo 29/10 10:30, "comando duplo"): excluir faixa ou funcao pede confirmacao
  const [confirmar, setConfirmar] = useState<Confirmacao | null>(null)
  // s69 / 2r (Marcelo 29/10/2026 08:46, "sempre exibir todas"): desfeito o esconder da 2l -- toda funcao aparece,
  // com ou sem cargos (o cartao ja diz "sem cargos").

  const acao = useMutation({
    mutationFn: (f: () => Promise<unknown>) => f(),
    onMutate: () => setErro(null),
    onError: (e) => setErro(mensagem(e)),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: BANDS_QUERY_KEY })
      void qc.invalidateQueries({ queryKey: ORG_CHART_QUERY_KEY })
    },
  })
  const faz = (f: () => Promise<unknown>) => acao.mutate(f)

  if (q.isPending) return <LoadingState />
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  const faixas = q.data

  const soltar = (alvo: JobBand, i: number) => {
    const a = arraste
    setArraste(null)
    if (!a) return
    if (a.tipo === 'funcao' && a.de !== alvo.id) faz(() => updateFunction(a.id, { bandId: alvo.id }))
    if (a.tipo === 'faixa' && a.id !== alvo.id) faz(() => updateBand(a.id, { position: i + 1 }))
  }

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Box>
        <Button size="small" component={RouterLink} to="/cargos" startIcon={<ArrowBack fontSize="small" />}>
          {t.bands.back}
        </Button>
      </Box>
      <Box>
        <Typography variant="h1" sx={{ fontSize: '1.5rem' }}>
          {t.bands.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {pode ? t.bands.hint : t.bands.hintReadOnly}
        </Typography>
      </Box>
      {erro && (
        <Alert severity="warning" data-testid="faixas-erro" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}
      <Stack spacing={1} data-testid="faixas">
        {faixas.map((b, i) => (
          <Paper
            key={b.id}
            variant="outlined"
            data-testid={`faixa-${b.id}`}
            data-posicao={b.position}
            onDragOver={(e) => {
              if (arraste) e.preventDefault()
            }}
            onDrop={(e) => {
              e.preventDefault()
              soltar(b, i)
            }}
            sx={{ p: 1.25, borderTop: `3px solid ${brand.navy}`, bgcolor: i % 2 === 0 ? '#F1F5F9' : '#F8FAFC' }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: { md: 300 }, flexShrink: 0 }}>
                {pode && (
                  <Box
                    component="span"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer?.setData('text/plain', b.id)
                      setArraste({ tipo: 'faixa', id: b.id })
                    }}
                    aria-label={t.bands.dragBand(b.name)}
                    title={t.bands.dragBand(b.name)}
                    sx={{ cursor: 'grab', color: 'text.secondary', display: 'flex' }}
                  >
                    <DragIndicator fontSize="small" />
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', width: 18 }}>
                  {b.position}
                </Typography>
                {renomeando === b.id ? (
                  <NomeEditavel
                    valor={b.name}
                    rotulo={t.bands.renameBand(b.name)}
                    onSalvar={(v) => {
                      setRenomeando(null)
                      if (v.trim() !== '' && v.trim() !== b.name) faz(() => updateBand(b.id, { name: v }))
                    }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 700, color: brand.navy, flex: 1, overflowWrap: 'anywhere' }}>
                    {b.name}
                  </Typography>
                )}
                {pode && (
                  <>
                    <Tooltip title={t.bands.rename}>
                      <IconButton size="small" aria-label={t.bands.renameBand(b.name)} onClick={() => setRenomeando(b.id)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" aria-label={t.bands.moveUp(b.name)} disabled={i === 0} onClick={() => faz(() => updateBand(b.id, { position: i }))}>
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={t.bands.moveDown(b.name)}
                      disabled={i === faixas.length - 1}
                      onClick={() => faz(() => updateBand(b.id, { position: i + 2 }))}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                    <IconButton size="small" aria-label={t.bands.deleteBand(b.name)} onClick={() => setConfirmar({ mensagem: t.bands.confirmDeleteBand(b.name), acao: () => faz(() => deleteBand(b.id)) })}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Stack>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                {b.functions.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t.bands.noFunctions}
                  </Typography>
                )}
                {b.functions.map((f) => (
                  <CartaoFuncao
                    key={f.id}
                    f={f}
                    faixas={faixas}
                    pode={pode}
                    onArrastar={() => setArraste({ tipo: 'funcao', id: f.id, de: b.id })}
                    onMover={(bandId) => faz(() => updateFunction(f.id, { bandId }))}
                    onRenomear={(nome) => faz(() => updateFunction(f.id, { name: nome }))}
                    onExcluir={() => setConfirmar({ mensagem: t.bands.confirmDeleteFunction(f.name), acao: () => faz(() => deleteFunction(f.id)) })}
                  />
                ))}
                {pode && (
                  <NovaFuncao
                    onCriar={(nome) => faz(() => createFunction(nome, b.id))}
                  />
                )}
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
      {pode && (
        <Box
          component="form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            if (novaFaixa.trim() === '') return
            faz(() => createBand(novaFaixa))
            setNovaFaixa('')
          }}
          sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <TextField
            size="small"
            placeholder={t.bands.newBand}
            value={novaFaixa}
            onChange={(e) => setNovaFaixa(e.target.value)}
            disabled={faixas.length >= BAND_MAX}
            slotProps={{ htmlInput: { maxLength: 80, 'aria-label': t.bands.newBand } }}
          />
          <Button type="submit" variant="outlined" size="small" startIcon={<Add fontSize="small" />} disabled={faixas.length >= BAND_MAX}>
            {t.bands.addBand}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t.bands.bandCount(faixas.length, BAND_MAX)}
          </Typography>
        </Box>
      )}
      <ConfirmDialog pedido={confirmar} fechar={() => setConfirmar(null)} />
    </Stack>
  )
}
